import React, { useEffect, useRef, useState } from 'react';

import {
  CalendarOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  PlayCircleFilled,
  RollbackOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  App,
  Avatar,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApiErrorData } from 'models/ApiResponse';
import { DashboardConfig } from 'models/DashboardConfig';
import {
  ActiveVideo,
  AdminIncident,
  AdminUserDetail,
  ProfileStatusCode,
  ProfileStatusEntry,
  RevertSentinel,
  ScoreLetter,
  UpdatableProfileStatusCode,
} from 'models/Lead';
import { User } from 'models/User';

import { LeadService } from 'services/LeadService';
import { UserService } from 'services/UserService';

import { useThemeLayoutConfig } from '../../../../config/ThemeDashboardLayoutConfig';
import appI18n from '../../../../config/i18n';
import StatusIndicator from '../StatusIndicator';
import SectionActions from './SectionActions';
import SectionEditFooter from './SectionEditFooter';

const { Text } = Typography;

interface Props {
  detail: AdminUserDetail;
  statuses: ProfileStatusEntry[];
  onUpdated: (next: AdminUserDetail) => void;
}

const SCORE_PROFILE_OPTIONS = [1, 2, 3, 4, 5];

const SCORE_LETTER_OPTIONS: ScoreLetter[] = ['A', 'B', 'C'];

// Field names that belong to the evaluation section (snake_case + camelCase)
const EVALUATION_FIELD_NAMES = new Set([
  'status_code',
  'statusCode',
  'score_profile',
  'scoreProfile',
  'score_aptitude',
  'scoreAptitude',
  'score_sales',
  'scoreSales',
  'hr_notes',
  'hrNotes',
]);

// ── State machine: allowed transitions per current state ──────────────────
type TransitionTarget = UpdatableProfileStatusCode | RevertSentinel;

const ALLOWED_TRANSITIONS: Record<ProfileStatusCode, TransitionTarget[]> = {
  video_pending: ['qualified', 'rejected', 'blacklisted'],
  qualified: ['contacted', 'video_pending', 'blacklisted'],
  contacted: ['training', 'qualified', 'blacklisted'],
  training: ['trainee_worker', 'no_show', 'contacted', 'blacklisted'],
  trainee_worker: ['generic_worker', 'training', 'blacklisted'],
  generic_worker: ['active_worker', 'trainee_worker', 'blacklisted'],
  rejected: ['video_pending', 'blacklisted'],
  no_show: ['training', 'blacklisted'],
  active_worker: ['generic_worker', 'blacklisted'],
  blacklisted: ['__revert__'],
  lead: [],
  profile_completed: [],
};

function getAllowedTargets(currentCode: ProfileStatusCode): TransitionTarget[] {
  return ALLOWED_TRANSITIONS[currentCode] ?? [];
}

// Pairs that represent a backwards transition (not forward progress)
const PREVIOUS_STATE_PAIRS: Array<[ProfileStatusCode, string]> = [
  ['qualified', 'video_pending'],
  ['contacted', 'qualified'],
  ['training', 'contacted'],
  ['trainee_worker', 'training'],
  ['generic_worker', 'trainee_worker'],
  ['rejected', 'video_pending'],
  ['no_show', 'training'],
  ['active_worker', 'generic_worker'],
];

function isPreviousStateTransition(
  currentCode: ProfileStatusCode,
  target: string
): boolean {
  return PREVIOUS_STATE_PAIRS.some(
    ([from, to]) => from === currentCode && to === target
  );
}

function findEvaluationIncident(
  incidents: AdminIncident[]
): AdminIncident | null {
  const sorted = [...incidents].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  for (const inc of sorted) {
    if (
      inc.snapshot &&
      typeof inc.snapshot === 'object' &&
      (inc.snapshot as Record<string, unknown>).section === 'evaluation'
    ) {
      return inc;
    }
    if (inc.fieldName && EVALUATION_FIELD_NAMES.has(inc.fieldName)) {
      return inc;
    }
  }
  return null;
}

const buildValues = (detail: AdminUserDetail) => ({
  status: detail.profile.statusCode,
  scoreProfile: detail.profile.scoreProfile ?? null,
  scoreAptitude: detail.profile.scoreAptitude ?? null,
  scoreSales: detail.profile.scoreSales ?? null,
  hrNotes: detail.profile.hrNotes || '',
});

// ── Score sub-components ───────────────────────────────────────────────────

const ScoreRow = ({
  value,
  onChange,
  disabled,
}: {
  value: number | null | undefined;
  onChange?: (value: number) => void;
  disabled?: boolean;
}) => (
  <Radio.Group
    value={value ?? null}
    optionType="button"
    buttonStyle="solid"
    onChange={onChange ? (e) => onChange(e.target.value) : undefined}
    disabled={disabled}
    options={SCORE_PROFILE_OPTIONS.map((n) => ({ label: n, value: n }))}
  />
);

const ScoreLetterRow = ({
  value,
  onChange,
  disabled,
}: {
  value: ScoreLetter | null | undefined;
  onChange?: (value: ScoreLetter) => void;
  disabled?: boolean;
}) => (
  <Radio.Group
    value={value ?? null}
    optionType="button"
    buttonStyle="solid"
    onChange={onChange ? (e) => onChange(e.target.value) : undefined}
    disabled={disabled}
    options={SCORE_LETTER_OPTIONS.map((l) => ({ label: l, value: l }))}
  />
);

// ── Last editor card ───────────────────────────────────────────────────────

function LastEditorCard({
  incident,
  formatDate,
}: {
  incident: AdminIncident | null;
  formatDate: (iso: string) => string;
}) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const { theme } = useThemeLayoutConfig();
  const dashboardConfig: DashboardConfig = useSelector(
    (state: any) => state.dashboardConfig
  );
  const primaryColor =
    dashboardConfig?.primaryColor ||
    (theme.token?.colorPrimary as string | undefined) ||
    '#1677ff';

  const cache = useRef<Record<number, User>>({});
  const [adminUser, setAdminUser] = useState<User | null>(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  const changedBy = incident?.changedBy ?? null;

  useEffect(() => {
    if (changedBy === null || changedBy === undefined) {
      setAdminUser(null);
      return;
    }

    // Usar cache si ya está disponible
    if (cache.current[changedBy]) {
      setAdminUser(cache.current[changedBy]);
      return;
    }

    let cancelled = false;
    setAdminUser(null);
    setLoadingAdmin(true);

    UserService.findById(changedBy).then((res) => {
      if (cancelled) return;
      if (res.success) {
        const user = res.data as User;
        cache.current[changedBy] = user;
        setAdminUser(user);
      }
      setLoadingAdmin(false);
    });

    return () => {
      cancelled = true;
    };
  }, [changedBy]);

  const base =
    'scenes.leads.components.detail_lead_modal.sections.evaluation.last_editor';

  let displayName: string;
  let initials: string;
  let dateStr: string;
  let avatarBg: string;

  if (!incident) {
    displayName = t(`${base}.no_user`);
    initials = '?';
    dateStr = t(`${base}.no_date`);
    avatarBg = 'rgba(0,0,0,0.25)';
  } else if (incident.changedBy !== null) {
    if (adminUser && !loadingAdmin) {
      const firstInitial = adminUser.name?.charAt(0)?.toUpperCase() ?? 'A';
      const lastInitial = adminUser.lastname?.charAt(0)?.toUpperCase() ?? '';
      initials = lastInitial ? `${firstInitial}${lastInitial}` : firstInitial;
      displayName = `${adminUser.name} ${adminUser.lastname}`.trim();
    } else {
      displayName = t(`${base}.admin_fallback`, { id: incident.changedBy });
      initials = 'A';
    }
    dateStr = formatDate(incident.createdAt);
    avatarBg = primaryColor;
  } else {
    displayName = t(`${base}.system`);
    initials = 'SI';
    dateStr = formatDate(incident.createdAt);
    avatarBg = '#8c8c8c';
  }

  return (
    <div className="lead-last-editor-card">
      <Avatar
        size={36}
        style={{ backgroundColor: avatarBg, color: '#fff', flexShrink: 0 }}
        icon={!incident ? <UserOutlined /> : undefined}
      >
        {incident ? initials : undefined}
      </Avatar>
      <div className="lead-last-editor-info">
        <span className="lead-last-editor-name">{displayName}</span>
        <span className="lead-last-editor-date">{dateStr}</span>
      </div>
    </div>
  );
}

// ── Funnel Status Panel ────────────────────────────────────────────────────

interface FunnelStatusPanelProps {
  currentCode: ProfileStatusCode;
  selectedStatus: string;
  statuses: ProfileStatusEntry[];
  editing: boolean;
  saving: boolean;
  onChange: (value: string) => void;
}

function FunnelStatusPanel({
  currentCode,
  selectedStatus,
  statuses,
  editing,
  saving,
  onChange,
}: FunnelStatusPanelProps) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });

  const panelBase =
    'scenes.leads.components.detail_lead_modal.sections.evaluation.funnel_panel';
  const sceneBase = 'scenes.leads.scene.status_codes';

  const allowedTargets = getAllowedTargets(currentCode);
  const hasTargets = allowedTargets.length > 0;
  const disabled = !editing || saving;

  const getStatusEntry = (code: string): ProfileStatusEntry | undefined =>
    statuses.find((s) => s.code === code);

  // Prioriza el label del backend (catálogo) según idioma activo; cae a i18n local y luego al code.
  const getStatusLabel = (code: string): string => {
    const entry = statuses.find((s) => s.code === code);
    const backendLabel = entry
      ? i18n.language === 'en'
        ? entry.labelEn
        : entry.labelEs
      : undefined;
    if (backendLabel && backendLabel.trim().length > 0) return backendLabel;
    return t(`${sceneBase}.${code}`, { defaultValue: code });
  };

  const getTransitionDescription = (
    current: ProfileStatusCode,
    target: string
  ): string => {
    const prevKey = `${panelBase}.previous_state_descriptions.${current}.${target}`;
    const prevDesc = t(prevKey, { defaultValue: '' });
    if (prevDesc) return prevDesc;
    return t(`${panelBase}.status_descriptions.${target}`, {
      defaultValue: '',
    });
  };

  // Current status display
  const currentEntry = getStatusEntry(currentCode);
  const currentColor = currentEntry?.color ?? '#8c8c8c';

  return (
    <div className="lead-funnel-panel">
      {/* Current status badge */}
      <div className="lead-funnel-current-badge">
        <StatusIndicator
          color={currentColor}
          label={getStatusLabel(currentCode)}
        />
      </div>

      {/* Transition options */}
      {!editing ? null : !hasTargets ? (
        <Text type="secondary" className="lead-funnel-no-transitions">
          {t(`${panelBase}.no_transitions`)}
        </Text>
      ) : (
        <>
          <Text type="secondary" className="lead-funnel-instruction">
            {t(`${panelBase}.instruction`)}
          </Text>
          <div className="lead-funnel-options">
            {allowedTargets.map((target) => {
              const isRevert = target === '__revert__';
              const isPrevious =
                !isRevert && isPreviousStateTransition(currentCode, target);
              const isSpecialBack = isRevert || isPrevious;
              const targetEntry = isSpecialBack
                ? undefined
                : getStatusEntry(target);
              const targetColor = targetEntry?.color ?? '#8c8c8c';
              const isSelected = selectedStatus === target;

              return (
                <button
                  key={target}
                  type="button"
                  disabled={disabled}
                  className={[
                    'lead-funnel-option',
                    isSelected ? 'lead-funnel-option--selected' : '',
                    disabled ? 'lead-funnel-option--disabled' : '',
                    isSpecialBack ? 'lead-funnel-option--revert' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => !disabled && onChange(target)}
                >
                  <div className="lead-funnel-option-header">
                    {isSpecialBack ? (
                      <span className="lead-funnel-option-revert-label">
                        <RollbackOutlined
                          style={{ marginRight: 6, fontSize: 13 }}
                        />
                        {t(`${panelBase}.previous_state_label`)}
                      </span>
                    ) : (
                      <StatusIndicator
                        color={targetColor}
                        label={getStatusLabel(target)}
                      />
                    )}
                  </div>
                  <div className="lead-funnel-option-desc">
                    {isRevert
                      ? t(`${panelBase}.revert_desc`)
                      : getTransitionDescription(currentCode, target)}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Video card sub-components ──────────────────────────────────────────────

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = m.toString().padStart(2, '0');
  const ss = s.toString().padStart(2, '0');
  if (h > 0) {
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
}

function VideoCardContent({
  activeVideo,
}: {
  activeVideo: ActiveVideo | null;
}) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const [isPlaying, setIsPlaying] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const base =
    'scenes.leads.components.detail_lead_modal.sections.evaluation.video_interview';

  const formatDate = (iso: string) => {
    const fmt =
      i18n.language === 'en' ? 'MM/DD/YYYY h:mm A' : 'DD/MM/YYYY HH:mm';
    return dayjs(iso).format(fmt);
  };

  const handleDownloadVideo = async () => {
    if (!activeVideo) return;
    setDownloading(true);
    const messageKey = 'downloading-video';
    message.loading({
      content: t(`${base}.download.downloading`),
      key: messageKey,
      duration: 0,
    });
    try {
      const res = await fetch(activeVideo.presignedVideoUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const subtype = activeVideo.mimeType?.split('/')[1] || 'mp4';
      const ext = subtype.split(';')[0].trim();
      const filename = `entrevista-${activeVideo.id}.${ext}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a delay so the browser has time to start the download.
      // Revoking synchronously right after click() races the download and can
      // abort it for larger files (flaky failures).
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
      message.destroy(messageKey);
      message.success(t(`${base}.download.success`));
    } catch (err) {
      message.destroy(messageKey);
      message.error(t(`${base}.download.failed`));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      {activeVideo ? (
        <div className="lead-video-frame">
          <div className="lead-video-wrapper">
            {isPlaying ? (
              <video
                controls
                autoPlay
                src={activeVideo.presignedVideoUrl}
                poster={activeVideo.presignedThumbnailUrl}
              />
            ) : (
              <button
                className="lead-video-thumbnail"
                onClick={() => setIsPlaying(true)}
                aria-label={t(`${base}.play_button_label`)}
                type="button"
              >
                {activeVideo.presignedThumbnailUrl ? (
                  <img
                    src={activeVideo.presignedThumbnailUrl}
                    alt=""
                    className="lead-video-thumb-img"
                  />
                ) : (
                  <div className="lead-video-thumb-placeholder" />
                )}
                <div className="lead-video-thumb-overlay" />
                <PlayCircleFilled className="lead-video-play-icon" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="lead-video-placeholder">
          <div className="lead-video-placeholder-icon">
            <VideoCameraOutlined />
          </div>
          <span className="lead-video-placeholder-title">
            {t(`${base}.placeholder_title`)}
          </span>
        </div>
      )}

      <div className="lead-video-footer">
        <div className="lead-video-meta">
          <div className="lead-video-meta-item">
            <CalendarOutlined className="lead-video-meta-icon" />
            <div className="lead-video-meta-text">
              <span className="lead-video-meta-label">
                {t(`${base}.meta_uploaded_at`)}
              </span>
              <span className="lead-video-meta-value">
                {activeVideo?.uploadedAt
                  ? formatDate(activeVideo.uploadedAt)
                  : '—'}
              </span>
            </div>
          </div>
          <div className="lead-video-meta-item">
            <ClockCircleOutlined className="lead-video-meta-icon" />
            <div className="lead-video-meta-text">
              <span className="lead-video-meta-label">
                {t(`${base}.meta_duration`)}
              </span>
              <span className="lead-video-meta-value">
                {formatDuration(activeVideo?.durationSeconds)}
              </span>
            </div>
          </div>
        </div>

        {activeVideo && (
          <div className="lead-video-actions">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadVideo}
              loading={downloading}
              block
            >
              {t(`${base}.download.button`)}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

function EvaluationSection({ detail, statuses, onUpdated }: Props) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>(
    detail.profile.statusCode
  );

  const watchedScoreProfile = Form.useWatch('scoreProfile', form);
  const watchedScoreAptitude = Form.useWatch('scoreAptitude', form);
  const watchedScoreSales = Form.useWatch('scoreSales', form);

  useEffect(() => {
    const vals = buildValues(detail);
    form.setFieldsValue(vals);
    setSelectedStatus(vals.status);
  }, [detail, form]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    if (saving) return;
    const vals = buildValues(detail);
    form.setFieldsValue(vals);
    setSelectedStatus(vals.status);
    setEditing(false);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    form.setFieldValue('status', value);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const currentCode = detail.profile.statusCode;
      const panelBase =
        'scenes.leads.components.detail_lead_modal.sections.evaluation.funnel_panel';

      const payload: Parameters<typeof LeadService.updateProfileEvaluation>[1] =
        {
          scoreProfile: values.scoreProfile ?? null,
          scoreAptitude: (values.scoreAptitude as ScoreLetter) ?? null,
          scoreSales: (values.scoreSales as ScoreLetter) ?? null,
          hrNotes: values.hrNotes || '',
        };

      // Only include status if it changed from current
      if (values.status !== currentCode) {
        payload.status = values.status as
          | UpdatableProfileStatusCode
          | RevertSentinel;
      }

      const response = await LeadService.updateProfileEvaluation(
        detail.user.id,
        payload
      );

      if (response.success) {
        const data = response.data as any;
        const statusUpdate = data?.statusUpdate;

        if (statusUpdate && statusUpdate.applied === false) {
          // Status change was silently rejected by backend
          message.warning(
            t(`${panelBase}.status_update_no_op`, {
              reason: statusUpdate.reason ?? 'no_change',
            }),
            4
          );
        } else {
          message.success(
            t(
              'scenes.leads.components.detail_lead_modal.sections.evaluation.messages.update_success'
            )
          );
        }

        const refreshed = await LeadService.getById(detail.user.id);
        if (refreshed.success) {
          onUpdated(refreshed.data as AdminUserDetail);
        }
        setEditing(false);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t(
              'scenes.leads.components.detail_lead_modal.sections.evaluation.messages.update_failed'
            ),
          3.5
        );
      }
    } catch {
      // validation error — antd form handles display
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso: string) => {
    const fmt =
      i18n.language === 'en' ? 'MM/DD/YYYY h:mm A' : 'DD/MM/YYYY HH:mm';
    return dayjs(iso).format(fmt);
  };

  const guideBase =
    'scenes.leads.components.detail_lead_modal.sections.evaluation.score_guide';

  const ScoreGuideCollapse = (
    <Collapse
      ghost
      size="small"
      className="lead-score-guide-collapse"
      items={[
        {
          key: 'guide',
          label: (
            <span className="lead-score-guide-header">
              <InfoCircleOutlined className="lead-score-guide-icon" />
              <strong>{t(`${guideBase}.title`)}</strong>
            </span>
          ),
          children: (
            <div className="lead-score-guide-content">
              <div className="lead-score-guide-subtitle">
                {t(`${guideBase}.numeric_scale_subtitle`)}
              </div>
              <div className="lead-score-guide-item">
                {t(`${guideBase}.numeric_1`)}
              </div>
              <div className="lead-score-guide-item">
                {t(`${guideBase}.numeric_5`)}
              </div>
              <div className="lead-score-guide-subtitle lead-score-guide-subtitle--second">
                {t(`${guideBase}.letter_scale_subtitle`)}
              </div>
              <div className="lead-score-guide-item">
                {t(`${guideBase}.letter_A`)}
              </div>
              <div className="lead-score-guide-item">
                {t(`${guideBase}.letter_B`)}
              </div>
              <div className="lead-score-guide-item">
                {t(`${guideBase}.letter_C`)}
              </div>
            </div>
          ),
        },
      ]}
    />
  );

  const evaluationBase =
    'scenes.leads.components.detail_lead_modal.sections.evaluation';

  const evaluationIncident = findEvaluationIncident(detail.incidents);

  // `editable=false` (Card en modo lectura) omite el binding interactivo (el
  // valor viene directo de `detail`, sin `onChange`). `editable=true` (Card en
  // modo edicion) registra un campo oculto en el `formInstance` para que
  // `validateFields`/`getFieldsValue` lo incluyan al guardar.
  const ScoreField = ({
    name,
    label,
    value,
    editable,
    formInstance,
  }: {
    name: 'scoreProfile';
    label: string;
    value: number | null | undefined;
    editable: boolean;
    formInstance?: FormInstance;
  }) => (
    <Col xs={24}>
      {editable && (
        <Form.Item name={name} hidden>
          <Input />
        </Form.Item>
      )}
      <div className="lead-score-field-row">
        <span className="lead-score-field-label">{label}</span>
        <div className={!editable ? 'lead-score-readonly' : ''}>
          <ScoreRow
            value={value ?? null}
            onChange={
              editable ? (v) => formInstance?.setFieldValue(name, v) : undefined
            }
          />
        </div>
      </div>
    </Col>
  );

  const ScoreLetterField = ({
    name,
    label,
    value,
    editable,
    formInstance,
  }: {
    name: 'scoreAptitude' | 'scoreSales';
    label: string;
    value: ScoreLetter | null | undefined;
    editable: boolean;
    formInstance?: FormInstance;
  }) => (
    <Col xs={24}>
      {editable && (
        <Form.Item name={name} hidden>
          <Input />
        </Form.Item>
      )}
      <div className="lead-score-field-row">
        <span className="lead-score-field-label">{label}</span>
        <div className={!editable ? 'lead-score-readonly' : ''}>
          <ScoreLetterRow
            value={value ?? null}
            onChange={
              editable ? (v) => formInstance?.setFieldValue(name, v) : undefined
            }
          />
        </div>
      </div>
    </Col>
  );

  return (
    <Row gutter={[16, 16]}>
      {/* ── Card: Gestión y evaluación (editable in-place) ─────── */}
      <Col xs={24} lg={12}>
        <Card
          className={
            editing
              ? 'lead-section-card lead-section-card--editing'
              : 'lead-section-card'
          }
          size="small"
          title={
            <Space size={8}>
              <TrophyOutlined />
              <span>{t(`${evaluationBase}.title`)}</span>
            </Space>
          }
          extra={<SectionActions editing={editing} onEdit={handleEdit} />}
        >
          {/* Bloque "Calificado por" — siempre solo lectura */}
          <Form layout="vertical" style={{ marginBottom: 4 }}>
            <Form.Item label={t(`${evaluationBase}.last_editor.label`)}>
              <LastEditorCard
                incident={evaluationIncident}
                formatDate={formatDate}
              />
            </Form.Item>
          </Form>

          <Form
            form={form}
            layout="vertical"
            disabled={!editing}
            style={{ marginTop: 16 }}
          >
            {/* Hidden field keeps status in form values */}
            <Form.Item name="status" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              label={t(`${evaluationBase}.labels.funnel_status`)}
              style={{ marginBottom: 0 }}
            >
              <FunnelStatusPanel
                currentCode={detail.profile.statusCode}
                selectedStatus={
                  editing ? selectedStatus : detail.profile.statusCode
                }
                statuses={statuses}
                editing={editing}
                saving={saving}
                onChange={handleStatusChange}
              />
            </Form.Item>

            <div className="lead-score-guide-spacer">{ScoreGuideCollapse}</div>

            <Row gutter={16}>
              <ScoreField
                name="scoreProfile"
                label={t(`${evaluationBase}.labels.score_profile`)}
                value={
                  editing
                    ? (watchedScoreProfile as number | null | undefined)
                    : detail.profile.scoreProfile
                }
                editable={editing}
                formInstance={form}
              />
              <ScoreLetterField
                name="scoreAptitude"
                label={t(`${evaluationBase}.labels.score_aptitude`)}
                value={
                  editing
                    ? (watchedScoreAptitude as ScoreLetter | null | undefined)
                    : detail.profile.scoreAptitude
                }
                editable={editing}
                formInstance={form}
              />
              <ScoreLetterField
                name="scoreSales"
                label={t(`${evaluationBase}.labels.score_sales`)}
                value={
                  editing
                    ? (watchedScoreSales as ScoreLetter | null | undefined)
                    : detail.profile.scoreSales
                }
                editable={editing}
                formInstance={form}
              />
            </Row>

            <Form.Item
              name="hrNotes"
              label={t(`${evaluationBase}.labels.hr_notes`)}
              rules={[
                {
                  max: 5000,
                  message: t(`${evaluationBase}.validations.hr_notes_max`),
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                maxLength={5000}
                showCount={editing}
                disabled={!editing || saving}
                placeholder={t(`${evaluationBase}.placeholders.hr_notes`)}
              />
            </Form.Item>

            {editing && (
              <SectionEditFooter
                saving={saving}
                onCancel={handleCancel}
                onSave={handleSave}
              />
            )}
          </Form>
        </Card>
      </Col>

      {/* ── Card: Video de entrevista (siempre solo lectura) ────── */}
      <Col xs={24} lg={12}>
        <Card
          className="lead-section-card lead-video-card"
          size="small"
          title={
            <Space size={8}>
              <VideoCameraOutlined />
              <span>{t(`${evaluationBase}.video_interview.title`)}</span>
            </Space>
          }
        >
          <VideoCardContent activeVideo={detail.activeVideo} />
        </Card>
      </Col>
    </Row>
  );
}

export default EvaluationSection;
